import React, { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useIntegrationStatus } from '../../contexts/IntegrationStatusContext';
import {
  CodeCommitConnectPayload,
  CodeCommitConnection,
  CodeCommitRoleSetup,
  OrgCodeCommitStatus,
  codecommitAuthModeLabel,
  codecommitConnectionDisplayName,
} from '../../types/codecommit';
import CodeCommitConnectForm from './CodeCommitConnectForm';

interface OrgCodeCommitConnectionProps {
  organizationId: string;
  readOnly?: boolean;
}

const DISCONNECT_BTN =
  'px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-100 dark:disabled:hover:bg-red-900/30';

const OrgCodeCommitConnection: React.FC<OrgCodeCommitConnectionProps> = ({ organizationId, readOnly = false }) => {
  const api = useAuthenticatedApi();
  const { refresh: refreshIntegrationStatus } = useIntegrationStatus();
  const [status, setStatus] = useState<OrgCodeCommitStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<CodeCommitConnection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<OrgCodeCommitStatus>(apiEndpoints.orgCodeCommit.status(organizationId, true));
      setStatus(res);
    } catch (e) {
      console.error('Failed to fetch AWS CodeCommit status', e);
    } finally {
      setLoading(false);
    }
  }, [api, organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadRoleSetup = useCallback(
    () => api.get<CodeCommitRoleSetup>(apiEndpoints.orgCodeCommit.roleSetup(organizationId)),
    [api, organizationId]
  );

  const connect = async (payload: CodeCommitConnectPayload) => {
    await api.post(apiEndpoints.orgCodeCommit.connect(organizationId), payload);
    setShowConnectForm(false);
    refreshIntegrationStatus();
    await refresh();
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await api.delete(apiEndpoints.orgCodeCommit.disconnect(organizationId, disconnectTarget.id));
      refreshIntegrationStatus();
      await refresh();
      setDisconnectTarget(null);
    } catch (e) {
      console.error('Failed to disconnect AWS CodeCommit', e);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-ui-background-tertiary rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-ui-background-tertiary rounded w-32"></div>
      </div>
    );
  }

  const connections = status?.connections ?? [];
  const hasConnections = !!status?.connected && connections.length > 0;

  const form = (
    <CodeCommitConnectForm
      loadRoleSetup={loadRoleSetup}
      onSubmit={connect}
      onCancel={() => setShowConnectForm(false)}
      readOnly={readOnly}
    />
  );

  if (!hasConnections) {
    return (
      <div className="space-y-3">
        {!showConnectForm ? (
          <>
            <p className="text-sm text-ui-text-secondary">
              Connect an AWS account to import CodeCommit repositories, commit changes, and open
              pull requests for all members.
            </p>
            <button
              type="button"
              onClick={() => setShowConnectForm(true)}
              disabled={readOnly}
              className="px-4 py-2 rounded-lg transition-colors shadow-sm bg-brand-primary hover:bg-brand-primaryDark text-black text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-brand-primary"
            >
              Connect AWS CodeCommit
            </button>
          </>
        ) : (
          form
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {connections.map((conn) => (
          <div key={conn.id} className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-ui-text-primary truncate">
                  {codecommitConnectionDisplayName(conn)}
                </div>
                <div className="text-xs text-ui-text-secondary mt-0.5 flex flex-wrap items-center gap-x-2">
                  <span className="px-1.5 py-0.5 rounded border border-ui-border-primary text-ui-text-tertiary">
                    {codecommitAuthModeLabel(conn.auth_mode)}
                  </span>
                  {conn.connected_at && <span>Connected {new Date(conn.connected_at).toLocaleDateString()}</span>}
                </div>
                {conn.caller_arn && (
                  <div className="text-xs text-ui-text-tertiary mt-0.5 font-mono truncate">{conn.caller_arn}</div>
                )}
                {conn.credentials_invalid && (
                  <div className="text-xs mt-0.5 text-red-600 dark:text-red-400">
                    Credentials are invalid or revoked — reconnect to restore access
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDisconnectTarget(conn)}
                disabled={readOnly}
                className={DISCONNECT_BTN}
              >
                Disconnect
              </button>
            </div>
          </div>
        ))}

        {showConnectForm ? (
          <div className="pt-2">{form}</div>
        ) : (
          <button
            type="button"
            onClick={() => setShowConnectForm(true)}
            disabled={readOnly}
            className="px-4 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary text-sm"
          >
            Add another connection
          </button>
        )}
      </div>

      {disconnectTarget && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !disconnecting && setDisconnectTarget(null)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-ui-border-primary">
                <h3 className="text-base font-semibold text-ui-text-primary">Disconnect AWS CodeCommit</h3>
              </div>
              <div className="px-5 py-4 text-sm text-ui-text-secondary">
                Disconnect {codecommitConnectionDisplayName(disconnectTarget)} from this organization?
                Repositories from this connection will stop syncing until it is reconnected.
              </div>
              <div className="px-5 py-4 flex justify-end gap-2 border-t border-ui-border-primary">
                <button
                  className="px-4 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => setDisconnectTarget(null)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrgCodeCommitConnection;
